import { useEffect, useState } from "react";
import { Clock, CursorClick, CurrencyDollar, Confetti, Lightning } from "@phosphor-icons/react";
import { toast } from "sonner";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "./ui/sheet";
import { linkAdSets } from "../lib/api";

function Row({ label, value, color = "#ccff00", icon: Icon }) {
  return (
    <div className="bg-[#121212] border border-white/5 p-3">
      <div className="flex items-center justify-between mb-2">
        <div className="label-mono text-[9px]">{label}</div>
        {Icon && <Icon size={12} style={{ color }} weight="duotone" />}
      </div>
      <div className="font-display text-xl font-black" style={{ color }}>
        {value}
      </div>
    </div>
  );
}

export default function LinkHistoryDrawer({ open, onOpenChange, link }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !link?.id) return;
    let cancelled = false;
    setLoading(true);
    linkAdSets(link.id)
      .then((d) => {
        if (!cancelled) setData(d);
      })
      .catch((err) => {
        toast.error(err.response?.data?.detail || "Failed to load history");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, link?.id]);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-2xl bg-[#0a0a0a] border-l border-white/10 text-white overflow-y-auto"
        data-testid="link-history-drawer"
      >
        <SheetHeader>
          <SheetTitle className="font-display text-2xl text-white flex items-center gap-2">
            <Clock size={22} weight="fill" className="text-[#ccff00]" />
            {link?.name || "History"}
          </SheetTitle>
          <SheetDescription className="text-zinc-500 text-sm">
            Every ad set generated for this affiliate URL, ranked by revenue.
          </SheetDescription>
        </SheetHeader>

        {loading && (
          <div className="text-center text-zinc-500 font-mono text-sm py-16">
            loading history…
          </div>
        )}

        {data && !loading && (
          <div className="mt-6 space-y-6">
            {/* KPIs */}
            <div className="grid grid-cols-4 gap-2">
              <Row label="ad sets" value={data.total_ad_sets} icon={Lightning} color="#ccff00" />
              <Row label="clicks" value={data.total_clicks} icon={CursorClick} color="#ff007f" />
              <Row label="conv" value={data.total_conversions} icon={Confetti} color="#7dd3fc" />
              <Row
                label="revenue"
                value={`$${data.total_revenue?.toFixed(2) || "0.00"}`}
                icon={CurrencyDollar}
                color="#f472b6"
              />
            </div>

            {data.ad_sets?.length === 0 && (
              <div className="text-center py-16 text-zinc-500 font-mono text-sm">
                No ads generated for this link yet.
              </div>
            )}

            <div className="space-y-3">
              {data.ad_sets?.map((a) => (
                <div
                  key={a.id}
                  data-testid={`history-adset-${a.id}`}
                  className="bg-[#121212] border border-white/5 hover:border-white/20 p-4 transition-colors"
                >
                  <div className="flex items-start justify-between gap-3 mb-2">
                    <div className="min-w-0 flex-1">
                      <div className="font-display font-bold text-white truncate">
                        {a.campaign_name || a.scrape_title || "(unnamed)"}
                      </div>
                      <div className="font-mono text-[10px] text-zinc-500 truncate mt-0.5">
                        {a.url}
                      </div>
                    </div>
                    <div className="font-mono text-[10px] text-zinc-500 flex-shrink-0">
                      {new Date(a.created_at).toLocaleDateString()}
                    </div>
                  </div>
                  <div className="flex flex-wrap items-center gap-1.5 mb-2">
                    {a.platforms?.map((p) => (
                      <span
                        key={p}
                        className="font-mono text-[9px] uppercase tracking-wider px-1.5 py-0.5 bg-black/40 border border-white/10 text-zinc-400"
                      >
                        {p}
                      </span>
                    ))}
                  </div>
                  <div className="flex flex-wrap items-center gap-4 font-mono text-xs">
                    <span className="text-zinc-500">
                      clicks:{" "}
                      <span className="text-[#ccff00] font-bold">
                        {a.clicks}
                      </span>
                    </span>
                    <span className="text-zinc-500">
                      conv:{" "}
                      <span className="text-cyan-300 font-bold">
                        {a.conversions}
                      </span>
                    </span>
                    <span className="text-zinc-500">
                      cvr:{" "}
                      <span className="text-zinc-200">
                        {a.cvr?.toFixed(2)}%
                      </span>
                    </span>
                    <span className="text-zinc-500">
                      revenue:{" "}
                      <span className="text-pink-400 font-bold">
                        ${a.revenue?.toFixed(2)}
                      </span>
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
